import { queryAll, query } from '../utils/utils.js';

export class TopMenu {

    isMobile = false;
    menuItems = query('.subheader-nav .main-row ').querySelectorAll('.menu-item-has-children');
    subMenuItems = query('.navbar .subheader-nav-mobile ').querySelectorAll('.menu-item-has-children');
    subItemContentCollection = query('.subheader-nav .sub-row .sub-item-content-collection');
    subMenus = null;
    currentMenuID = null;
    currentMenu = null;
    subnav = query('.navbar .subheader-nav');
    indicator = query('.subheader-nav .indicator');
    submenuContainer = query('.subheader-nav .sub-row');
    breadcrumbs = null;
    burgerMenuToggle = query('.navbar .nav-container .burger');
    submenuMobile = query('.navbar .subheader-nav-mobile');
    activeSubMenu = null;
    activeSubMenuContent = null;

    boundDocumentClickClose = null;

    constructor() {

        this.fixMenuIcons();
        this.detectMobile();
        this.initBurgerMenu();
        this.initMegaMenu();
        this.initMobileMegaMenu();
        this.initSearchToggle();
        this.initBreadCrumbs();
        this.initScrollHideNav();

        window.addEventListener('resize', () => {
            this.detectMobile();
            this.updateFixedHeaderOffset();
        });

        window.addEventListener('orientationchange', () => {
            this.detectMobile();
            this.updateFixedHeaderOffset();
        });

        // remove submenuitemcollection from DOM //
        this.subItemContentCollection.remove();

        requestAnimationFrame(() => {
            this.updateFixedHeaderOffset();
        });
    }

    fixMenuIcons() {
        // move icon classes to child <a> element for styling
        queryAll('.icon-cta-item').forEach(elm => {
            const childHref = elm.querySelector('a');
            const parentClasses = elm.classList;

            for (let i = 0; i < parentClasses.length; i++) {
                if ((parentClasses[i].indexOf('icon-cta-item') == -1) && (parentClasses[i].indexOf('icon-') > -1)) {
                    childHref.classList.add(parentClasses[i]);
                    elm.classList.remove(parentClasses[i]);
                }
            }
        });
    }

    getDesktopMenubar() {
        return document.querySelector('#main-nav[role="menubar"]');
    }

    getDesktopTopLevelLinks() {
        const menubar = this.getDesktopMenubar();
        if (!menubar) return [];
        return Array.from(menubar.querySelectorAll(':scope > .menu-item > a[role="menuitem"]'));
    }

    getMobileMenubar() {
        return document.querySelector('#main-nav-mobile[role="menubar"]');
    }

    getMobileTopLevelLinks() {
        const menubar = this.getMobileMenubar();
        if (!menubar) return [];
        return Array.from(menubar.querySelectorAll(':scope > .menu-item > a[role="menuitem"]'));
    }

    setMenubarRovingTabindex(links, active) {
        links.forEach((a) => { a.tabIndex = -1; });
        if (active) active.tabIndex = 0;
    }

    syncDesktopMegaAria() {
        const menubar = this.getDesktopMenubar();
        if (!menubar) return;

        menubar.querySelectorAll('a[role="menuitem"][aria-haspopup="true"]').forEach((a) => {
            const item = a.closest('.menu-item');
            const open = item && item.classList.contains('active');
            a.setAttribute('aria-expanded', open ? 'true' : 'false');
        });

        queryAll('.subheader-nav .sub-menu[role="menu"]').forEach((ul) => {
            const labelled = ul.getAttribute('aria-labelledby');
            const trigger = labelled ? document.getElementById(labelled) : null;
            const open = trigger && trigger.getAttribute('aria-expanded') === 'true';
            ul.setAttribute('aria-hidden', open ? 'false' : 'true');
        });
    }

    isLikelyVisibleForTabOrder(el) {
        if (!(el instanceof HTMLElement)) return false;
        let node = el;
        while (node && node !== document.body) {
            const st = window.getComputedStyle(node);
            if (st.display === 'none' || st.visibility === 'hidden') {
                return false;
            }
            node = node.parentElement;
        }
        const rect = el.getBoundingClientRect();
        return rect.width > 0 || rect.height > 0;
    }

    /**
     * Focusable items inside an open desktop mega panel (nav column + visible content CTAs), in tab order.
     */
    collectDesktopPanelTabbables(panel) {
        if (!panel) return [];
        const out = [];
        panel.querySelectorAll('.nav-col .sub-menu > .menu-item > a[href]').forEach((a) => {
            if (this.isLikelyVisibleForTabOrder(a)) {
                out.push(a);
            }
        });
        const activeContent = panel.querySelector('.content-col .sub-item-content.active');
        if (activeContent) {
            activeContent.querySelectorAll('a[href], button:not([disabled])').forEach((el) => {
                if (this.isLikelyVisibleForTabOrder(el)) {
                    out.push(el);
                }
            });
        }
        return out;
    }

    syncDesktopMenubarTabOrder() {
        if (this.isMobile) return;
        const menubar = this.getDesktopMenubar();
        if (!menubar) return;

        const openId = this.currentMenuID;
        const topLinks = this.getDesktopTopLevelLinks();

        topLinks.forEach((a) => {
            const item = a.closest('.menu-item');
            const id = item?.dataset?.id;
            if (openId == null) {
                a.removeAttribute('tabindex');
            } else if (String(id) === String(openId)) {
                a.tabIndex = 0;
            } else {
                a.tabIndex = -1;
            }
        });

        document.querySelectorAll(
            '.subheader-nav .main-row .search-module a[href], .subheader-nav .main-row .search-module button'
        ).forEach((el) => {
            if (openId == null) {
                el.removeAttribute('tabindex');
            } else {
                el.tabIndex = -1;
            }
        });

        const panels = document.querySelectorAll('.subheader-nav .sub-row .sub-menu-item');
        panels.forEach((subPanel) => {
            const panelIsOpen = openId != null && String(subPanel.dataset.id) === String(openId);

            subPanel.querySelectorAll('.nav-col .sub-menu > .menu-item > a[href]').forEach((a) => {
                a.tabIndex = panelIsOpen ? 0 : -1;
            });

            subPanel.querySelectorAll('.content-col .sub-item-content').forEach((block) => {
                const ctasActive =
                    panelIsOpen && block.classList.contains('active');
                block.querySelectorAll('a[href], button:not([disabled])').forEach((el) => {
                    el.tabIndex = ctasActive ? 0 : -1;
                });
            });
        });
    }

    focusFirstSubmenuLink(menuDataId) {
        const panel = Array.from(this.subMenus || []).find((s) => s.dataset.id === String(menuDataId));
        if (!panel) return;
        const tabbables = this.collectDesktopPanelTabbables(panel);
        if (!tabbables.length) return;
        tabbables[0].focus();
    }

    handleDesktopMegaTabKeydown(e) {
        if (e.key !== 'Tab' || this.isMobile || this.currentMenuID == null) return;

        const trigger = this.currentMenu?.querySelector('a[role="menuitem"]');
        const panel = Array.from(this.subMenus || []).find((s) => s.dataset.id === String(this.currentMenuID));
        if (!trigger || !panel) return;

        const tabbables = this.collectDesktopPanelTabbables(panel);
        if (!tabbables.length) return;

        const target = e.target;
        if (target !== trigger && !panel.contains(target)) return;

        const firstPanel = tabbables[0];
        const lastPanel = tabbables[tabbables.length - 1];

        if (e.shiftKey) {
            if (target === firstPanel) {
                e.preventDefault();
                trigger.focus();
            } else if (target === trigger) {
                e.preventDefault();
                lastPanel.focus();
            }
            return;
        }

        if (target === lastPanel) {
            e.preventDefault();
            trigger.focus();
            return;
        }

        if (target === trigger) {
            e.preventDefault();
            firstPanel.focus();
        }
    }

    openDesktopMegaMenuForMenuItem(menuItem, { focusPanel = false } = {}) {
        const childHref = menuItem.querySelector('a');
        const currBounds = menuItem.getBoundingClientRect();
        const menuBounds = query('.subheader-nav .main-row ').getBoundingClientRect();

        if (menuItem.dataset.id === this.currentMenuID) {
            this.collapseMenuDesktop();
            childHref?.focus();
            return;
        }

        if (this.currentMenuID == null) {
            this.indicator.style.transition = 'all 0';
            this.indicator.style.left = (currBounds.left - menuBounds.left) + 'px';
            this.indicator.style.width = currBounds.width + 'px';

            setTimeout(() => {
                this.indicator.style.transition = 'all 0.4s cubic-bezier(0.76, 0, 0.24, 1)';
                this.indicator.style.height = '4px';
            }, 300);
        }

        this.currentMenuID = menuItem.dataset.id;
        this.menuItems.forEach((item) => {
            if (item.dataset.id === this.currentMenuID) {
                item.classList.add('active');
                this.currentMenu = item;
                this.indicator.style.left = (currBounds.left - menuBounds.left) + 'px';
                this.indicator.style.width = currBounds.width + 'px';
            } else {
                item.classList.remove('active');
            }
        });

        this.subMenus.forEach((subMenu) => {
            if (subMenu.dataset.id === this.currentMenuID) {
                subMenu.classList.add('active');
                this.openSubMenuItemDesktop(subMenu.dataset.id, 0);
            } else {
                subMenu.classList.remove('active');
            }
        });

        this.syncDesktopMegaAria();
        this.syncDesktopMenubarTabOrder();
        if (focusPanel) {
            requestAnimationFrame(() => this.focusFirstSubmenuLink(menuItem.dataset.id));
        }
    }

    handleDesktopMenubarKeydown(e) {
        const menubar = this.getDesktopMenubar();
        if (!menubar) return;
        const target = e.target;
        if (!target.matches('a[role="menuitem"]')) return;

        const inSubmenu = !!target.closest('.subheader-nav .sub-menu[role="menu"]') &&
            !this.isTopLevelMenubarLink(target, menubar);

        if (inSubmenu) {
            this.handleDesktopSubmenuKeydown(e, target, menubar);
            return;
        }

        if (!this.isTopLevelMenubarLink(target, menubar)) return;

        const links = this.getDesktopTopLevelLinks();
        const idx = links.indexOf(target);
        if (idx === -1) return;

        const hasPopup = target.getAttribute('aria-haspopup') === 'true';

        switch (e.key) {
            case 'ArrowRight': {
                e.preventDefault();
                links[(idx + 1) % links.length].focus();
                break;
            }
            case 'ArrowLeft': {
                e.preventDefault();
                links[(idx - 1 + links.length) % links.length].focus();
                break;
            }
            case 'ArrowDown':
            case 'Enter':
            case ' ': {
                if (!hasPopup) {
                    if (e.key === ' ') {
                        e.preventDefault();
                        target.click();
                    }
                    break;
                }
                e.preventDefault();
                const menuItem = target.closest('.menu-item');
                if (this.currentMenuID === menuItem.dataset.id) {
                    if (e.key === 'ArrowDown') {
                        this.focusFirstSubmenuLink(menuItem.dataset.id);
                    } else {
                        this.collapseMenuDesktop();
                        target.focus();
                    }
                } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                    this.openDesktopMegaMenuForMenuItem(menuItem, {
                        focusPanel: e.key === 'ArrowDown',
                    });
                }
                break;
            }
            case 'Escape': {
                if (this.currentMenuID !== null) {
                    e.preventDefault();
                    const toFocus = this.currentMenu?.querySelector('a[role="menuitem"]');
                    this.collapseMenuDesktop();
                    (toFocus || target).focus();
                }
                break;
            }
            case 'Home': {
                e.preventDefault();
                links[0].focus();
                break;
            }
            case 'End': {
                e.preventDefault();
                links[links.length - 1].focus();
                break;
            }
            default:
                break;
        }
    }

    isTopLevelMenubarLink(target, menubar) {
        return target.parentElement?.parentElement === menubar;
    }

    handleDesktopSubmenuKeydown(e, target, menubar) {
        const submenuUl = target.closest('ul.sub-menu[role="menu"]');
        if (!submenuUl) return;

        const links = Array.from(submenuUl.querySelectorAll(':scope > .menu-item > a[role="menuitem"]'));
        let i = links.indexOf(target);
        if (i === -1) return;

        const labelId = submenuUl.getAttribute('aria-labelledby');
        const parentTrigger = labelId ? document.getElementById(labelId) : null;
        const topLinks = this.getDesktopTopLevelLinks();
        const pIdx = parentTrigger ? topLinks.indexOf(parentTrigger) : -1;

        switch (e.key) {
            case 'ArrowDown': {
                e.preventDefault();
                const next = links[(i + 1) % links.length];
                next.focus();
                break;
            }
            case 'ArrowUp': {
                e.preventDefault();
                const prev = links[(i - 1 + links.length) % links.length];
                prev.focus();
                break;
            }
            case 'ArrowRight': {
                if (pIdx === -1) return;
                e.preventDefault();
                const nextTop = topLinks[(pIdx + 1) % topLinks.length];
                this.collapseMenuDesktop();
                nextTop.focus();
                break;
            }
            case 'ArrowLeft': {
                if (pIdx === -1) return;
                e.preventDefault();
                const prevTop = topLinks[(pIdx - 1 + topLinks.length) % topLinks.length];
                this.collapseMenuDesktop();
                prevTop.focus();
                break;
            }
            case 'Escape': {
                e.preventDefault();
                this.collapseMenuDesktop();
                if (parentTrigger) {
                    parentTrigger.focus();
                }
                break;
            }
            case 'Home': {
                e.preventDefault();
                if (links[0]) {
                    links[0].focus();
                }
                break;
            }
            case 'End': {
                e.preventDefault();
                const last = links[links.length - 1];
                if (last) {
                    last.focus();
                }
                break;
            }
            case 'Enter':
            case ' ': {
                const li = target.closest('.menu-item');
                if (li?.classList.contains('has-content')) {
                    e.preventDefault();
                    this.openSubMenuItemDesktop(li.dataset.menu, li.dataset.id);
                } else if (e.key === ' ') {
                    e.preventDefault();
                    target.click();
                }
                break;
            }
            default:
                break;
        }
    }

    initDesktopMenubarKeyboard() {
        const menubar = this.getDesktopMenubar();
        if (!menubar) return;

        // Top-level primary nav links stay tabbable together with the open
        // mega panel; tabindex is synchronized in syncDesktopMenubarTabOrder.
        menubar.addEventListener('keydown', (e) => this.handleDesktopMenubarKeydown(e));
    }

    /******************************* MEGA MENU DESKTOP **********************************/

    /*
    * Mega menu
    *****/
    initMegaMenu() {
        const subItemsContainer = query('.subheader-nav .sub-row .sub-items-container');

        this.boundDocumentClickClose = (e) => {
            if (!e.target.closest('.subheader-nav')) {
                this.collapseMenuDesktop();
            }
        };
        document.addEventListener('click', this.boundDocumentClickClose, true);

        // add plus/minus icons to menu items with children
        this.menuItems.forEach((menuItem, i) => {
            menuItem.dataset.id = i;

            const childHref = menuItem.querySelector('a');

            const cont = document.createElement('div');
            cont.classList.add('plus-minus');
            cont.setAttribute('aria-hidden', 'true');

            const plus = document.createElement('span');
            plus.classList.add('plus');
            plus.innerHTML = '+';
            cont.appendChild(plus);

            const minus = document.createElement('span');
            minus.classList.add('minus');
            minus.innerHTML = '–';
            cont.appendChild(minus);

            childHref.appendChild(cont);

            // move submenus to sub-items-container
            const l2Menu = menuItem.querySelector('.sub-menu');

            if (l2Menu) {

                const subDiv = document.createElement('div');
                subDiv.dataset.id = i;
                subDiv.classList.add('row');
                subDiv.classList.add('sub-menu-item');

                const panelId = childHref.getAttribute('aria-controls');
                if (panelId) {
                    subDiv.id = panelId;
                }

                const navCol = document.createElement('div');
                navCol.classList.add('nav-col');
                navCol.appendChild(l2Menu);
                subDiv.appendChild(navCol);

                const contentCol = document.createElement('div');
                contentCol.classList.add('content-col');
                subDiv.appendChild(contentCol);

                subItemsContainer.appendChild(subDiv);

                l2Menu.querySelectorAll('.menu-item').forEach((l3MenuItem, j) => {
                    l3MenuItem.dataset.menu = i;
                    l3MenuItem.dataset.id = j;

                    const l3MenuItemContent = this.subItemContentCollection.querySelector('.sub-item-content.sub-' + i + '.item-' + j);
                    if (l3MenuItemContent) {
                        const contentClone = l3MenuItemContent.cloneNode(true);
                        contentClone.style.display = 'flex';
                        contentClone.dataset.menu = i;
                        contentClone.dataset.id = j;
                        contentCol.appendChild(contentClone);
                        l3MenuItem.classList.add('has-content');
                        l3MenuItem.dataset.menu = i;
                        l3MenuItem.dataset.id = j;
                        l3MenuItem.addEventListener('click', (ev) => {
                            ev.preventDefault();
                            this.openSubMenuItemDesktop(ev.currentTarget.dataset.menu, ev.currentTarget.dataset.id);
                        });
                    } else {
                        l3MenuItem.classList.add('no-content');
                    }
                });

            }

            menuItem.addEventListener('click', (e) => {
                e.preventDefault();
                const curr = e.currentTarget;

                if (curr.dataset.id === this.currentMenuID) {
                    this.collapseMenuDesktop();
                    return;
                }

                this.openDesktopMegaMenuForMenuItem(curr);
            });

        });

        this.subMenus = query('.subheader-nav .sub-row').querySelectorAll('.sub-menu-item');

        this.boundDesktopMegaTabKeydown = (e) => this.handleDesktopMegaTabKeydown(e);
        this.subnav.addEventListener('keydown', this.boundDesktopMegaTabKeydown);

        this.syncDesktopMegaAria();
        this.initDesktopMenubarKeyboard();
    }

    openSubMenuItemDesktop(menuID, subMenuID) {

        let maxHeight = 0;
        this.subMenus.forEach((l2Menu) => {

            if (l2Menu.dataset.id == menuID) {
                l2Menu.classList.add('active');
                this.activeSubMenu = l2Menu;

                l2Menu.querySelectorAll('.menu-item').forEach((l3MenuItem, j) => {
                    if (l3MenuItem.dataset.menu == menuID && l3MenuItem.dataset.id == subMenuID) {
                        l3MenuItem.classList.add('active');

                    } else {
                        l3MenuItem.classList.remove('active');
                    }
                });

                l2Menu.querySelectorAll('.sub-item-content').forEach((l3MenuContent) => {
                    if (l3MenuContent.dataset.menu == menuID && l3MenuContent.dataset.id == subMenuID) {
                        l3MenuContent.classList.add('active');
                        this.activeSubMenuContent = l3MenuContent;
                        this.handleResize();

                        const l3MenuContentHeight = l3MenuContent.getBoundingClientRect().height;

                        if (l3MenuContentHeight > maxHeight) {
                            maxHeight = l3MenuContentHeight;
                        }

                    } else {
                        l3MenuContent.classList.remove('active');
                    }
                });

                const l2MenuHeight = l2Menu.getBoundingClientRect().height;
                if (l2MenuHeight > maxHeight) {
                    maxHeight = l2MenuHeight;
                }

            } else {
                l2Menu.classList.remove('active');
            }
        });
        this.submenuContainer.style.height = maxHeight + 64 + 'px';
        this.syncDesktopMenubarTabOrder();
    }

    collapseMenuDesktop() {
        this.menuItems.forEach((item) => item.classList.remove('active'));
        if (this.subMenus) {
            this.subMenus.forEach((sm) => sm.classList.remove('active'));
        }
        this.currentMenuID = null;
        this.currentMenu = null;
        this.indicator.style.height = '0px';
        this.submenuContainer.style.height = '64px';
        this.activeSubMenuContent = null;
        this.syncDesktopMegaAria();
        this.syncDesktopMenubarTabOrder();
    }

    /************************* BURGER MENU *************************************************************************************/

    setBurgerOpenState(isOpen) {
        this.burgerMenuToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        this.burgerMenuToggle.setAttribute('aria-label', isOpen ? 'Close the menu' : 'Open the menu');
    }

    // Toggle the inert attribute so collapsed panels are fully removed from
    // the keyboard tab order and the accessibility tree. Using inert avoids a
    // focus trap where visually hidden children (height:0 / transform) remain
    // focusable.
    setInert(el, inert) {
        if (!el) return;
        if (inert) {
            el.setAttribute('inert', '');
        } else {
            el.removeAttribute('inert');
        }
    }

    /*
    * init burger menu
    **/
    initBurgerMenu() {

        window.addEventListener('resize', () => {
            this.collapseBurgerMenu();
        });

        window.addEventListener('orientationchange', () => {
            this.collapseBurgerMenu();
        });

        this.burgerMenuToggle.addEventListener('click', (e) => {
            e.preventDefault();

            if (this.burgerMenuToggle.classList.contains('active')) {
                this.collapseBurgerMenu();
            } else {
                this.burgerMenuToggle.classList.add('active');
                this.submenuMobile.classList.add('active');
                this.setInert(this.submenuMobile, false);
                this.setBurgerOpenState(true);
            }
        });
    }

    collapseBurgerMenu() {
        this.burgerMenuToggle.classList.remove('active');
        this.submenuMobile.classList.remove('active');
        this.setInert(this.submenuMobile, true);
        this.setBurgerOpenState(false);

        this.submenuMobile.querySelectorAll('.sub-content-container').forEach((subContent) => {
            subContent.classList.remove('active');
            subContent.style.height = '0px';
        });

        this.closeEveryMobileL2Section();
    }

    /******************************* MEGA MENU MOBILE **********************************/

    syncMobileSubmenuAria(l2MenuItem, isOpen) {
        const trigger = l2MenuItem.querySelector(':scope > a[role="menuitem"][aria-haspopup="true"]');
        if (trigger) {
            trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }
        const sm = l2MenuItem.querySelector('.sub-menu[role="menu"]');
        if (sm) {
            sm.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        }
    }

    handleMobileMenubarKeydown(e) {
        const menubar = this.getMobileMenubar();
        if (!menubar || !this.submenuMobile.classList.contains('active')) return;
        const target = e.target;
        if (!target.matches('a[role="menuitem"]')) return;

        const inSubmenu = !!target.closest('#main-nav-mobile .sub-menu[role="menu"]') &&
            !this.isTopLevelMenubarLink(target, menubar);

        if (inSubmenu) {
            this.handleMobileSubmenuKeydown(e, target, menubar);
            return;
        }

        if (!this.isTopLevelMenubarLink(target, menubar)) return;

        const links = this.getMobileTopLevelLinks();
        const idx = links.indexOf(target);
        if (idx === -1) return;
        const hasPopup = target.getAttribute('aria-haspopup') === 'true';

        switch (e.key) {
            case 'ArrowRight': {
                e.preventDefault();
                links[(idx + 1) % links.length].focus();
                break;
            }
            case 'ArrowLeft': {
                e.preventDefault();
                links[(idx - 1 + links.length) % links.length].focus();
                break;
            }
            case 'ArrowDown':
            case 'Enter':
            case ' ': {
                if (!hasPopup) {
                    if (e.key === ' ') {
                        e.preventDefault();
                        target.click();
                    }
                    break;
                }
                e.preventDefault();
                target.click();
                break;
            }
            case 'Escape': {
                e.preventDefault();
                this.collapseBurgerMenu();
                this.burgerMenuToggle.focus();
                break;
            }
            case 'Home': {
                e.preventDefault();
                links[0].focus();
                break;
            }
            case 'End': {
                e.preventDefault();
                links[links.length - 1].focus();
                break;
            }
            default:
                break;
        }
    }

    handleMobileSubmenuKeydown(e, target, menubar) {
        const submenuUl = target.closest('ul.sub-menu[role="menu"]');
        if (!submenuUl || !menubar.contains(submenuUl)) return;

        const links = Array.from(submenuUl.querySelectorAll(':scope > .menu-item > a[role="menuitem"]'));
        let i = links.indexOf(target);
        if (i === -1) return;

        const labelId = submenuUl.getAttribute('aria-labelledby');
        const parentTrigger = labelId ? document.getElementById(labelId) : null;
        const topLinks = this.getMobileTopLevelLinks();
        const pIdx = parentTrigger ? topLinks.indexOf(parentTrigger) : -1;

        switch (e.key) {
            case 'ArrowDown': {
                e.preventDefault();
                const next = links[(i + 1) % links.length];
                this.setMenubarRovingTabindex(links, next);
                next.focus();
                break;
            }
            case 'ArrowUp': {
                e.preventDefault();
                const prev = links[(i - 1 + links.length) % links.length];
                this.setMenubarRovingTabindex(links, prev);
                prev.focus();
                break;
            }
            case 'ArrowRight': {
                if (pIdx === -1) return;
                e.preventDefault();
                const nextTop = topLinks[(pIdx + 1) % topLinks.length];
                this.closeEveryMobileL2Section();
                nextTop.focus();
                break;
            }
            case 'ArrowLeft': {
                if (pIdx === -1) return;
                e.preventDefault();
                const prevTop = topLinks[(pIdx - 1 + topLinks.length) % topLinks.length];
                this.closeEveryMobileL2Section();
                prevTop.focus();
                break;
            }
            case 'Escape': {
                e.preventDefault();
                this.closeEveryMobileL2Section();
                if (parentTrigger) {
                    parentTrigger.focus();
                }
                break;
            }
            case 'Home': {
                e.preventDefault();
                if (links[0]) {
                    this.setMenubarRovingTabindex(links, links[0]);
                    links[0].focus();
                }
                break;
            }
            case 'End': {
                e.preventDefault();
                const last = links[links.length - 1];
                if (last) {
                    this.setMenubarRovingTabindex(links, last);
                    last.focus();
                }
                break;
            }
            case 'Enter':
            case ' ': {
                const li = target.closest('.menu-item');
                if (li?.classList.contains('has-content')) {
                    e.preventDefault();
                    this.openSubMenuContentMobile(li.dataset.menu, li.dataset.id);
                } else if (e.key === ' ') {
                    e.preventDefault();
                    target.click();
                }
                break;
            }
            default:
                break;
        }
    }

    initMobileMenubarKeyboard() {
        const menubar = this.getMobileMenubar();
        if (!menubar) return;
        // Top-level mobile menu items stay tabbable (tabindex=0) so Tab
        // visits every item. Arrow keys continue to work below for
        // horizontal navigation.
        menubar.addEventListener('keydown', (e) => this.handleMobileMenubarKeydown(e));
    }

    /**
     * Init Mobile Mega Menu
     */
    initMobileMegaMenu() {
        const mobileMenuItems = queryAll('.subheader-nav-mobile .mega-menu-mobile .menu-item-has-children');
        mobileMenuItems.forEach((l2MenuItem, i) => {
            const submenu = l2MenuItem.querySelector('.sub-menu');
            let hasLevel3 = false;

            l2MenuItem.dataset.id = i;

            const subMenuContainer = document.createElement('div');
            subMenuContainer.classList.add('sub-menu-container');
            submenu.parentNode.insertBefore(subMenuContainer, submenu);
            subMenuContainer.appendChild(submenu);
            subMenuContainer.style.height = '0px';

            const trigger = l2MenuItem.querySelector(':scope > a[role="menuitem"]');
            const panelId = trigger?.getAttribute('aria-controls');
            if (panelId) {
                subMenuContainer.id = panelId;
            }

            this.syncMobileSubmenuAria(l2MenuItem, false);

            trigger?.addEventListener('click', (e) => {
                e.preventDefault();
                const curr = e.currentTarget.parentNode;
                this.closeAllSubMenuItemsMobile();
                mobileMenuItems.forEach((item) => {
                    const subContainer = item.querySelector('.sub-menu-container');
                    const sm = item.querySelector('.sub-menu');

                    if (item == curr) {
                        item.classList.toggle('active');
                        sm.classList.toggle('active');
                    } else {
                        item.classList.remove('active');
                        sm.classList.remove('active');
                    }

                    subContainer.style.height = (item.classList.contains('active')) ? sm.getBoundingClientRect().height + 'px' : '0px';
                    subContainer.baseHeight = (item.classList.contains('active')) ? sm.getBoundingClientRect().height : 0;

                    this.syncMobileSubmenuAria(item, item.classList.contains('active'));

                    if (item == curr) {
                        const firstL3Item = item.querySelector('.sub-menu.active .menu-item.has-content:first-child a');
                        if (firstL3Item) firstL3Item.click();
                    }

                });
            });

            l2MenuItem.querySelectorAll('.menu-item').forEach((l3MenuItem, j) => {
                l3MenuItem.dataset.menu = i;
                l3MenuItem.dataset.id = j;

                const l3MenuItemContent = this.subItemContentCollection.querySelector('.sub-item-content.sub-' + i + '.item-' + j);
                if (l3MenuItemContent) {
                    if (!hasLevel3) hasLevel3 = true;

                    const contentClone = l3MenuItemContent.cloneNode(true);
                    contentClone.style.display = 'flex';
                    contentClone.dataset.menu = i;
                    contentClone.dataset.id = j;

                    const contentContainer = document.createElement('div');
                    contentContainer.classList.add('sub-item-content-container');
                    contentContainer.appendChild(contentClone);
                    contentContainer.style.height = 0;

                    l3MenuItem.appendChild(contentContainer);
                    l3MenuItem.classList.add('has-content');
                    l3MenuItem.dataset.menu = i;
                    l3MenuItem.dataset.id = j;
                    l3MenuItem.parentContainer = subMenuContainer;
                    l3MenuItem.addEventListener('click', (e) => {
                        let parentElement = e.target.parentNode;
                        if (parentElement.classList.contains('menu-item')) {
                            e.preventDefault();
                            this.openSubMenuContentMobile(e.currentTarget.dataset.menu, e.currentTarget.dataset.id);
                        }
                    });
                } else {
                    l3MenuItem.classList.add('no-content');
                }
            });

            if (hasLevel3) {
                l2MenuItem.subMenuContainer = subMenuContainer;
            }
        });

        this.initMobileMenubarKeyboard();
    }

    openSubMenuContentMobile(menuID, subMenuID) {

        const mobileMenuItems = queryAll('.subheader-nav-mobile .mega-menu-mobile .menu-item-has-children');
        mobileMenuItems.forEach((l2MenuItem) => {

            const subMenuContainer = l2MenuItem.subMenuContainer;
            let talliedHeight = (subMenuContainer) ? subMenuContainer.baseHeight : 0;

            l2MenuItem.querySelectorAll('.menu-item').forEach((l3MenuItem) => {
                const l3MenuContentContainer = l3MenuItem.querySelector('.sub-item-content-container');
                const l3MenuContent = l3MenuItem.querySelector('.sub-item-content');

                if (l3MenuContent && l3MenuContentContainer) {

                    if (l3MenuItem.dataset.menu == menuID && l3MenuItem.dataset.id == subMenuID) {
                        if (l3MenuItem.classList.contains('active')) {
                            l3MenuItem.classList.remove('active');
                            l3MenuContent.classList.remove('active');
                            l3MenuContentContainer.classList.remove('active');
                        } else {
                            l3MenuItem.classList.add('active');
                            l3MenuContent.classList.add('active');
                            l3MenuContentContainer.classList.add('active');
                        }
                    } else {
                        l3MenuItem.classList.remove('active');
                        l3MenuContentContainer.classList.remove('active');
                        l3MenuContent.classList.remove('active');

                    }

                    if (l3MenuItem.classList.contains('active')) {
                        l3MenuContentContainer.style.height = l3MenuContent.getBoundingClientRect().height + 'px';
                        talliedHeight += l3MenuContent.getBoundingClientRect().height;
                    } else {
                        l3MenuContentContainer.style.height = 0;
                    }
                }
            });

            if (subMenuContainer) subMenuContainer.style.height = talliedHeight + 'px';

        });

    }

    closeAllSubMenuItemsMobile() {
        const mobileMenuItems = queryAll('.subheader-nav-mobile .mega-menu-mobile .menu-item-has-children');
        mobileMenuItems.forEach((l2MenuItem) => {
            const subMenuContainer = l2MenuItem.subMenuContainer;
            if (subMenuContainer) subMenuContainer.baseHeight = 0;

            l2MenuItem.querySelectorAll('.menu-item').forEach((l3MenuItem) => {
                const l3MenuContentContainer = l3MenuItem.querySelector('.sub-item-content-container');
                const l3MenuContent = l3MenuItem.querySelector('.sub-item-content');

                if (l3MenuContentContainer && l3MenuContent) {
                    l3MenuContent.classList.remove('active');
                    l3MenuContentContainer.classList.remove('active');
                    l3MenuContentContainer.style.height = 0;
                }

                l3MenuItem.classList.remove('active');
            });
        });
    }

    closeEveryMobileL2Section() {
        const mobileMenuItems = queryAll('.subheader-nav-mobile .mega-menu-mobile .menu-item-has-children');
        mobileMenuItems.forEach((l2MenuItem) => {
            l2MenuItem.classList.remove('active');
            const sm = l2MenuItem.querySelector('.sub-menu');
            if (sm) sm.classList.remove('active');
            const subContainer = l2MenuItem.querySelector('.sub-menu-container');
            if (subContainer) {
                subContainer.classList.remove('active');
                subContainer.style.height = '0px';
                subContainer.baseHeight = 0;
            }

            l2MenuItem.querySelectorAll('.menu-item').forEach((l3MenuItem) => {
                const l3MenuContentContainer = l3MenuItem.querySelector('.sub-item-content-container');
                const l3MenuContent = l3MenuItem.querySelector('.sub-item-content');
                if (l3MenuContentContainer && l3MenuContent) {
                    l3MenuContent.classList.remove('active');
                    l3MenuContentContainer.classList.remove('active');
                    l3MenuContentContainer.style.height = 0;
                }
                l3MenuItem.classList.remove('active');
            });

            this.syncMobileSubmenuAria(l2MenuItem, false);
        });
    }

    /*
    *  Hide nav on scroll down, show on scroll up
    */
    initScrollHideNav() {

        let prevScrollpos = window.pageYOffset;
        window.addEventListener('scroll', () => {
            if (this.isMobile) return false;

            const threshold = 100;
            let currentScrollPos = window.pageYOffset;
            const navbar = document.querySelector('.navbar.fixed-top .nav-container');

            if (prevScrollpos > currentScrollPos) {
                navbar.classList.remove('hidenav');
                query('body').classList.remove('nav-hidenav');

                if (this.breadcrumbs) {
                    this.breadcrumbs.classList.remove('hidenav');
                }

                document.querySelectorAll('.handle-hide-nav').forEach((el) => {
                    el.classList.remove('hidenav');
                });

            } else {
                if (currentScrollPos >= threshold)
                {
                    navbar.classList.add('hidenav');
                    query('body').classList.add('nav-hidenav');

                    document.querySelectorAll('.handle-hide-nav').forEach((el) => {
                        el.classList.add('hidenav');
                    });

                    if (this.breadcrumbs) {
                        this.breadcrumbs.classList.add('hidenav');
                    }
                }
            }
            prevScrollpos = currentScrollPos;
        });
    }

    /*
    *  init search toggle
    */
    initSearchToggle() {
        const searchBtns = document.querySelectorAll('.search-module .search-btn');
        const searchPanel = document.querySelector('.header-search-panel');
        const searchCloseBtn = searchPanel.querySelector('.search-close-btn');
        const searchBackground = searchPanel.querySelector('.background');
        const searchInput = searchPanel.querySelector('#header-searchInput');

        const closeSearchPanel = () => {
            searchPanel.classList.remove('show');
            searchPanel.classList.remove('show-search-suggestions');
            searchInput.value = '';
            searchInput.blur();
            this.setInert(searchPanel, true);
        };

        searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                closeSearchPanel();
            } else {
                e.currentTarget.dispatchEvent(new Event('click'));
            }
        });

        searchBtns.forEach((searchBtn) => {

            searchBtn.addEventListener('click', (e) => {
                this.collapseMenuDesktop();
                e.preventDefault();
                this.setInert(searchPanel, false);
                searchPanel.classList.add('show');
                searchInput.focus();

                document.addEventListener('keydown', (ev) => {
                    if (ev.key === 'Escape') {
                        closeSearchPanel();
                    }
                }, { once: true });
            });

        });

        searchCloseBtn.addEventListener('click', (e) => {
            e.preventDefault();
            closeSearchPanel();
        });

        searchBackground.addEventListener('click', (e) => {
            e.preventDefault();
            closeSearchPanel();
        });

        searchInput.addEventListener('click', (e) => {
            e.preventDefault();
            searchPanel.classList.add('show-search-suggestions');
        });
    }

    /*
    *  init breadcrub menu - move into subnav
    */
    initBreadCrumbs() {
        const yoastbreadcrumbs = query('.yoast-breadcrumbs');
        const container = document.createElement('div');
        container.classList.add('container');

        if (yoastbreadcrumbs) {
            query('body').classList.add('has-breadcrumbs');
            yoastbreadcrumbs.classList.add('breadcrumbs');
            container.appendChild(yoastbreadcrumbs);
            this.subnav.appendChild(container);

            this.breadcrumbs = yoastbreadcrumbs;

            const arrow = '<i class="icon-arrow-right"></i>';
            const arrowRegex = new RegExp('»', 'g');
            const arrowReplace = yoastbreadcrumbs.innerHTML.replace(arrowRegex, arrow);
            yoastbreadcrumbs.innerHTML = arrowReplace;
        }

        this.updateFixedHeaderOffset();
    }

    /*
    * Measure clearance from main to the bottom of the fixed header stack.
    */
    updateFixedHeaderOffset() {
        const navbar = document.querySelector('.navbar.fixed-top');
        const main = document.querySelector('.wp-site-blocks:not(.has-sticky-hero-nav) main.wp-block-group');

        if (!navbar || !main) {
            return;
        }

        const clearance = Math.ceil(
            navbar.getBoundingClientRect().bottom - main.getBoundingClientRect().top
        );

        document.documentElement.style.setProperty(
            '--bhs-fixed-header-offset',
            `${Math.max(clearance, 0)}px`
        );
    }

    /*
    * init menu detect
    ****/
    detectMobile() {
        const mobileDiv = query('.navbar .is-mobile');

        if (mobileDiv) {
            const display = window.getComputedStyle(mobileDiv).display;

            if (display === 'block') {
                this.isMobile = true;
            } else {
                this.isMobile = false;
            }
        }
    }

    resetMenu() {
        const navbar = document.querySelector('.navbar.fixed-top .nav-container');
        navbar.classList.remove('hidenav');

        if (this.breadcrumbs) {
            this.breadcrumbs.classList.remove('hidenav');
        }

        document.querySelectorAll('.handle-hide-nav').forEach((el) => {
            el.classList.remove('hidenav');
        });
    }

    handleResize() {

        if (this.activeSubMenuContent) {
            const children = this.activeSubMenuContent.querySelectorAll('div');
            let maxHeight = 0;

            children.forEach((child) => {
                const childHeight = child.getBoundingClientRect().height;
                if (childHeight > maxHeight) {
                    maxHeight = childHeight;
                }
            });
            this.activeSubMenuContent.style.height = maxHeight + 'px';
            this.activeSubMenu.querySelector('.nav-col').style.minHeight = maxHeight + 'px';
        }
    }


}
